import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Card,
    CardMedia,
    CardContent,
    CardActions,
    Typography,
    ListSubheader,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Button
} from '@mui/material';
import { FiberManualRecord } from '@mui/icons-material';

import { useEvents } from '../../context';

interface AboutCardProps {
    title: string;
    logo: string;
    about: Array<{ key: number; paragraph: string }>;
    read?: string;
    readMore?: boolean;
    list?: { title: string; items: Array<{ key: number; name: string }> };
    footer?: string;
    width?: number;
    contactLink?: string;
}

export const AboutCard: React.FC<AboutCardProps> = React.memo(
    ({ title, logo, about, readMore, list, footer, width, contactLink }) => {
        const { changeAboutLoad, reset } = useEvents();
        const [deviceWidth, setDeviceWidth] = useState(width && width < 600);
        useEffect(() => {
            setDeviceWidth(width && width < 600);
        }, [width]);
        return (
            <Card
                className={`about__card ${readMore && 'readMore'}`}
                id={title.toLocaleLowerCase()}
            >
                <CardMedia
                    sx={{
                        pt:
                            title === 'Prispek'
                                ? '30%'
                                : title === 'Spektra'
                                ? '25%'
                                : '40%',
                        m: 'auto',
                        overflow: 'show',
                        width: '100%'
                    }}
                    image={logo}
                />
                <CardContent>
                    {about.map(about => (
                        <Typography key={about.key} mb='0.5rem' paragraph>
                            {about.paragraph}
                        </Typography>
                    ))}
                    {list && (
                        <List
                            component='nav'
                            aria-labelledby='nested-list-subheader'
                            subheader={
                                <ListSubheader
                                    component='div'
                                    id='nested-list-subheader'
                                >
                                    <Typography color='textPrimary' variant='h6'>
                                        {list.title}
                                    </Typography>
                                </ListSubheader>
                            }
                        >
                            {list.items.map((item: any) => (
                                <ListItem key={item.key}>
                                    <ListItemIcon>
                                        <FiberManualRecord sx={{ color: '#000' }} />
                                    </ListItemIcon>
                                    <ListItemText primary={item.name} />
                                </ListItem>
                            ))}
                        </List>
                    )}
                    {footer && <Typography paragraph>{footer}</Typography>}
                </CardContent>
                <CardActions
                    disableSpacing
                    sx={{
                        display: 'flex',
                        width: '100%',
                        flexDirection: deviceWidth ? 'column' : 'row'
                    }}
                >
                    {contactLink ? (
                        <Button
                            fullWidth
                            href={contactLink}
                            target='_blank'
                            rel='noopener noreferrer'
                            variant='contained'
                            color='primary'
                            aria-label='book appointment'
                            onClick={reset}
                            sx={{
                                marginRight: deviceWidth ? 0 : '1rem',
                                flex: 1,
                                width: deviceWidth ? '100%' : 'auto'
                            }}
                        >
                            Book Appointment
                        </Button>
                    ) : (
                        <Button
                            component={Link}
                            to='/contact'
                            fullWidth
                            variant='contained'
                            color='primary'
                            aria-label='book appointment'
                            onClick={reset}
                            sx={{
                                marginRight: deviceWidth ? 0 : '1rem',
                                flex: 1,
                                width: deviceWidth ? '100%' : 'auto'
                            }}
                        >
                            Book Appointment
                        </Button>
                    )}
                    <div
                        style={{
                            display: 'flex',
                            flex: 1,
                            alignItems: 'center',
                            width: deviceWidth ? '100%' : 'auto',
                            marginTop: deviceWidth ? '0.5rem' : 0
                        }}
                    >
                        <Link
                            to={'/services#' + title}
                            style={{
                                marginRight: !readMore
                                    ? 0
                                    : deviceWidth
                                    ? '0.5rem'
                                    : '1rem',
                                width: deviceWidth ? '100%' : 'auto',
                                flex: !readMore ? 1 : deviceWidth ? 1 : 'auto'
                            }}
                        >
                            <Button
                                fullWidth
                                variant='contained'
                                color='secondary'
                                aria-label='services'
                                onClick={reset}
                                sx={{ color: 'white' }}
                            >
                                Services
                            </Button>
                        </Link>
                        {readMore && (
                            <Link
                                to={`/about#${title.toLocaleLowerCase()}`}
                                style={{
                                    flex: deviceWidth ? 1 : 'auto'
                                }}
                            >
                                <Button
                                    fullWidth
                                    variant='outlined'
                                    aria-label='read more'
                                    onClick={() => changeAboutLoad(title)}
                                >
                                    Read More
                                </Button>
                            </Link>
                        )}
                    </div>
                </CardActions>
            </Card>
        );
    }
);
